import { ActionReducerMapBuilder } from '@reduxjs/toolkit'
import { fromPairs } from 'lodash'
import flow from 'lodash/fp/flow'
import mapValues from 'lodash/fp/mapValues'
import set from 'lodash/fp/set'
import update from 'lodash/fp/update'
import get from 'lodash/get'
import has from 'lodash/has'
import isArray from 'lodash/isArray'
import pick from 'lodash/pick'
import { validate as isUuid } from 'uuid'

import {
    fetchFilterSettingsSuccess,
    fetchFiltersSuccess,
    resetFilters,
    resetLocalFilters,
    setFilters,
    setSavedFilterSet,
    updateFilter,
    updateFilterSettings,
    updateLocalFilter,
} from 'actions/ui/shared/filter'
import { AGGREGATION, DATES } from 'const/filters'
import {
    FILTER_SETTINGS,
    FILTER_UPDATED_AT,
    FILTERS,
    LOCAL_FILTERS,
} from 'const/reducerKeys'
import { getCurrentTimestamp } from 'helpers/utils'
import { PageId } from 'routing/pages'
import { FiltersState } from 'types'
import { Tab } from 'types/dashboards'

import { AVAILABLE_TAB_FILTERS, defaultDatesFilter } from '../defaults'
import { getPristineUiStateClone } from '../ui'

import { SharedState } from './state'

export const updateFilters = (
    targetFilters: any,
    sourceFilters: any
): boolean => {
    let updated = false
    if (targetFilters && sourceFilters) {
        Object.keys(targetFilters).forEach((filterKey) => {
            if (filterKey in sourceFilters) {
                targetFilters[filterKey] = sourceFilters[filterKey]
                updated = true
            } else if (
                filterKey !== DATES &&
                filterKey !== AGGREGATION &&
                !!targetFilters[filterKey]
            ) {
                // we need dates, so it's the only one we don't reset
                if (Array.isArray(targetFilters[filterKey])) {
                    updated = updated || targetFilters[filterKey].length > 0
                    targetFilters[filterKey] = []
                } else if (typeof targetFilters[filterKey] === 'object') {
                    updated =
                        updated ||
                        Object.keys(targetFilters[filterKey]).length > 0
                    targetFilters[filterKey] = {}
                } else if (typeof targetFilters[filterKey] === 'string') {
                    updated = updated || targetFilters[filterKey].length > 0
                    targetFilters[filterKey] = ''
                } else {
                    updated = updated || targetFilters[filterKey] !== undefined
                    targetFilters[filterKey] = undefined
                }
            }
        })
    }
    return updated
}

export const makeDesiredFiltersVisible = (
    targetFilterSettings: any,
    sourceFilters: any
): boolean => {
    let updated = false
    if (targetFilterSettings && sourceFilters) {
        if (!targetFilterSettings.displayState) {
            targetFilterSettings.displayState = {}
        }
        if (!targetFilterSettings.order) {
            targetFilterSettings.order = []
        }
        if (!targetFilterSettings.userAdded) {
            targetFilterSettings.userAdded = []
        }
        Object.keys(sourceFilters).forEach((filterKey) => {
            if (filterKey in targetFilterSettings.displayState) {
                targetFilterSettings.displayState[filterKey] = true
                updated = true
            }
            if (!targetFilterSettings.order.includes(filterKey)) {
                targetFilterSettings.order.push(filterKey)
                updated = true
            }
            if (!targetFilterSettings.userAdded.includes(filterKey)) {
                targetFilterSettings.userAdded.push(filterKey)
                updated = true
            }
        })
    }
    return updated
}

// Cases added to the shared/index createReducer; state is parent UI state
export const addFilterCases = (
    builder: ActionReducerMapBuilder<SharedState>
): void => {
    builder
        // Filters
        .addCase(fetchFiltersSuccess, (state, action) => {
            const {
                path,
                data: { filters },
            } = action.payload

            return flow(
                set([...path, FILTERS], filters),
                set([...path, FILTER_UPDATED_AT], getCurrentTimestamp())
            )(state)
        })
        .addCase(updateLocalFilter, (state, action) => {
            const {
                path,
                data: { key, value, automaticallyMade },
            } = action.payload

            let newState = flow(
                set([...path, LOCAL_FILTERS, key], value),
                set([...path, FILTER_UPDATED_AT], getCurrentTimestamp()),
                automaticallyMade
                    ? (inFlightState) => inFlightState
                    : set([...path, FILTER_SETTINGS, 'touched'], true),
                automaticallyMade
                    ? (inFlightState) => inFlightState
                    : set(
                          [...path, FILTER_SETTINGS, 'savedFilterSet'],
                          undefined
                      )
            )(state)

            const page = path[0] as PageId
            if (page === 'dashboardPage' || page === 'homePage') {
                newState = set([page, 'isDirty'], true, newState)
            }

            if (has(newState, [...path, 'tabs'])) {
                return update(
                    [...path, 'tabs'],
                    mapValues((tab: Partial<Tab>) => ({
                        ...tab,
                        loaded: false,
                    }))
                )(newState)
            }

            return newState
        })
        .addCase(updateFilter, (state, action) => {
            const {
                path,
                data: { key, value, automaticallyMade },
            } = action.payload

            const newState = flow(
                set([...path, FILTERS, key], value),
                set([...path, FILTER_UPDATED_AT], getCurrentTimestamp()),
                automaticallyMade
                    ? (inFlightState) => inFlightState
                    : set([...path, FILTER_SETTINGS, 'touched'], true),
                automaticallyMade
                    ? (inFlightState) => inFlightState
                    : set(
                          [...path, FILTER_SETTINGS, 'savedFilterSet'],
                          undefined
                      )
            )(state)

            if (has(newState, [...path, 'tabs'])) {
                return update(
                    [...path, 'tabs'],
                    mapValues((tab: Partial<Tab>) => ({
                        ...tab,
                        loaded: false,
                    }))
                )(newState)
            }

            return newState
        })
        .addCase(resetFilters, (state, action) => {
            const { path } = action.payload
            const defaultUiStateClone = getPristineUiStateClone()
            const pageDefaultState = get(defaultUiStateClone, path)

            if (pageDefaultState) {
                const newState = flow(
                    set([...path, FILTERS], pageDefaultState[FILTERS]),
                    set(
                        [...path, LOCAL_FILTERS],
                        pageDefaultState[LOCAL_FILTERS]
                    ),
                    set(
                        [...path, FILTER_SETTINGS],
                        pageDefaultState[FILTER_SETTINGS]
                    ),
                    set([...path, FILTER_UPDATED_AT], getCurrentTimestamp())
                )(state)

                if (has(newState, [...path, 'tabs'])) {
                    return update(
                        [...path, 'tabs'],
                        mapValues((tab: Partial<Tab>) => ({
                            ...tab,
                            loaded: false,
                        }))
                    )(newState)
                }

                return newState
            }

            return state
        })
        .addCase(setSavedFilterSet, (state, action) => {
            const { path, data: filterSet } = action.payload
            return flow(
                set([...path, FILTER_SETTINGS, 'savedFilterSet'], filterSet),
                set([...path, FILTER_UPDATED_AT], getCurrentTimestamp())
            )(state)
        })
        .addCase(setFilters, (state, action) => {
            const { path, data: filterSet } = action.payload
            const { config } = filterSet
            const { filters: actionFilters } = config

            const defaultUiStateClone = getPristineUiStateClone()
            const pageDefaultState = get(defaultUiStateClone, path)

            const defaultFilters: FiltersState =
                pageDefaultState?.[FILTERS] || {}
            const defaultLocalFilters: FiltersState =
                pageDefaultState?.[LOCAL_FILTERS] || {}
            const defaultOrder: string[] =
                pageDefaultState?.[FILTER_SETTINGS]?.order || []

            const desiredSettings = {
                anchored: [
                    ...(pageDefaultState?.[FILTER_SETTINGS]?.anchored || []),
                ],
                order: [...defaultOrder],
                displayState: defaultOrder.reduce(
                    (acc: { [key: string]: boolean }, key) => {
                        acc[key] = false
                        return acc
                    },
                    {}
                ),
                userAdded: [],
                touched: true,
                savedFilterSet: filterSet,
            }
            const desiredLocalFilters: FiltersState = JSON.parse(
                JSON.stringify(defaultLocalFilters)
            )
            const desiredFilters: FiltersState = JSON.parse(
                JSON.stringify(defaultFilters)
            )

            updateFilters(desiredLocalFilters, actionFilters)
            updateFilters(desiredFilters, actionFilters)
            makeDesiredFiltersVisible(desiredSettings, actionFilters)

            return flow(
                set([...path, FILTERS], desiredFilters),
                set([...path, LOCAL_FILTERS], desiredLocalFilters),
                set([...path, FILTER_SETTINGS], desiredSettings),
                set([...path, FILTER_UPDATED_AT], getCurrentTimestamp())
            )(state)
        })
        .addCase(resetLocalFilters, (state, action) => {
            const { path } = action.payload
            const defaultUiStateClone = getPristineUiStateClone()
            const pageDefaultState = get(defaultUiStateClone, path)

            if (pageDefaultState) {
                return set(
                    [...path, LOCAL_FILTERS],
                    pageDefaultState[LOCAL_FILTERS],
                    state
                )
            }

            // If path refers to a specific dashboard tab
            const pathTail = path.slice(-2)
            if (pathTail[0] === 'tabs' && isUuid(pathTail[1])) {
                const page = path[0]
                return flow(
                    set([page, 'isDirty'], true),
                    set([...path, LOCAL_FILTERS], {
                        ...fromPairs(
                            AVAILABLE_TAB_FILTERS.map((filter) => [
                                filter,
                                undefined,
                            ])
                        ),
                        [DATES]: defaultDatesFilter,
                    }),
                    set([...path, FILTER_SETTINGS], {
                        anchored: [],
                        order: AVAILABLE_TAB_FILTERS,
                        userAdded: [],
                        touched: false,
                        displayState: {},
                    })
                )(state)
            }

            return state
        })

        // Filter Settings
        .addCase(updateFilterSettings, (state, action) => {
            const {
                path,
                data: { anchored, order, displayState, userAdded },
            } = action.payload

            return update(
                [...path, FILTER_SETTINGS],
                (filterSettings) => ({
                    // Absorb anchored only when array is provided
                    anchored: isArray(anchored)
                        ? anchored
                        : filterSettings.anchored,

                    // Absorb order only when array is provided
                    order: isArray(order) ? order : filterSettings.order,

                    // Absorb displayState only when object is provided
                    displayState: displayState ?? filterSettings.displayState,

                    userAdded: isArray(userAdded)
                        ? userAdded
                        : filterSettings.userAdded,
                    touched: true,
                    savedFilterSet: undefined,
                }),
                state
            )
        })
        .addCase(fetchFilterSettingsSuccess, (state, action) => {
            const { path, data: filterSettings } = action.payload

            return set(
                [...path, FILTER_SETTINGS],
                pick(filterSettings, ['order', 'anchored', 'displayState']),
                state
            )
        })
}
